import { useEffect, useState } from "react";
import { Button, themeAtom } from "@humansignal/ui";
import { useAtomValue } from "jotai";
import { useAPI, useUpdatePageTitle } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { useTranslation } from "../../i18n/LocaleProvider";
import { useBranding } from "../../providers/BrandingProvider";
import { spaNavigate } from "../../utils/navigation";
import { JoinProjectModal } from "../Discover/JoinProjectModal";
import { RequestLanguageModal } from "./RequestLanguageModal";
import "./HomePage.prefix.css";

const defaultLockup = (dark: boolean) =>
  dark ? "/static/brand/afriannotate/lockup-dark.png?v=6" : "/static/brand/afriannotate/lockup.png?v=6";

type PublicProject = {
  id: number;
  title: string | null;
  description: string | null;
  organization: { id: number; title: string } | null;
  task_count: number;
  contributor_count: number;
  contribution_mode: "members_only" | "open";
  already_joined: boolean;
  join_status?: "none" | "active" | "pending" | "member";
  logo?: string;
  consent_requirements?: Array<Record<string, unknown>>;
};

/** Small square project logo, falling back to the initial. The
 *  fallback ALSO covers a src that fails to load — offline, an
 *  expired signed URL, or an un-cached logo used to leave the
 *  browser's broken-image glyph on every project card. */
export const LogoThumb = ({ src, title, size = 32 }: { src?: string; title: string | null; size?: number }) => {
  const initial = (title || "?").trim()[0]?.toUpperCase() ?? "?";
  const [failed, setFailed] = useState(false);
  // A new src gets a fresh chance (e.g. back online, refetched list).
  useEffect(() => {
    setFailed(false);
  }, [src]);
  const showImg = !!src && !failed;
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size / 4),
        flexShrink: 0,
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
        background: showImg ? "var(--color-neutral-background)" : "var(--color-primary-surface)",
        border: "1px solid var(--color-neutral-border)",
      }}
    >
      {showImg ? (
        <img
          src={src}
          alt=""
          onError={() => setFailed(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <span style={{ color: "#fff", fontWeight: 600, fontSize: Math.round(size * 0.42) }}>{initial}</span>
      )}
    </span>
  );
};

type TFn = (key: string, params?: Record<string, string | number>, fallback?: string) => string;

/** A public-project card. ``action`` is the trailing button (Open / Join). */
const PubCard = ({ t, p, action }: { t: TFn; p: PublicProject; action: React.ReactNode }) => (
  <div className="lsf-home-page__pubcard">
    <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
      <LogoThumb src={p.logo} title={p.title} />
      <div style={{ minWidth: 0 }}>
        <h4 className="lsf-home-page__pubcard-title">
          {p.title ?? t("pages.home.home_page.untitled_project", undefined, "Untitled project")}
        </h4>
        {p.organization?.title ? (
          <span className="lsf-home-page__pubcard-org">
            {t("pages.home.home_page.by_org", { org: p.organization.title }, `by ${p.organization.title}`)}
          </span>
        ) : null}
      </div>
    </div>
    <p className="lsf-home-page__pubcard-desc">
      {p.description || t("pages.home.home_page.no_description", undefined, "No description provided.")}
    </p>
    <div className="lsf-home-page__pubcard-meta">
      <span className="lsf-home-page__pubcard-stats">
        {t(
          "pages.home.home_page.stats",
          { tasks: p.task_count.toLocaleString(), contributors: p.contributor_count.toLocaleString() },
          `${p.task_count.toLocaleString()} tasks, ${p.contributor_count.toLocaleString()} contributors`,
        )}
      </span>
      {action}
    </div>
  </div>
);

/**
 * OrglessHome — the ``/`` landing for a signed-in user who belongs to no
 * organization. Instead of a dead-end "no org" card, it's a real home:
 * a welcome, a plain note that they're not in an org yet, and the open
 * projects they CAN contribute to. Joining runs through the same
 * ``JoinProjectModal`` (consent flow included) as Discover, so a
 * contributor can join without leaving the page.
 */
export const OrglessHome = () => {
  const { t } = useTranslation();
  useUpdatePageTitle("Home");
  const api = useAPI();
  const branding = useBranding();
  const { user } = useAuth();
  const theme = useAtomValue(themeAtom);
  const isDark = String(theme).toLowerCase() === "dark";

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email?.split("@")[0] || "there";
  const logoSrc = branding.logo_url || defaultLockup(isDark);

  const [projects, setProjects] = useState<PublicProject[] | null>(null);
  const [joined, setJoined] = useState<PublicProject[] | null>(null);
  const [joinTarget, setJoinTarget] = useState<PublicProject | null>(null);
  const [requestLangOpen, setRequestLangOpen] = useState(false);

  const load = () => {
    if (!user?.id) return;
    // Projects the contributor has already joined (their own work).
    api
      .callApi("discoverMine", { params: { page_size: 12 }, suppressError: true })
      .then((res: { results?: PublicProject[] } | PublicProject[] | undefined) => {
        const list = Array.isArray(res) ? res : (res?.results ?? []);
        setJoined(list as PublicProject[]);
      })
      .catch(() => setJoined([]));
    // What Discover shows: every public project (open to join, or members
    // only), less the ones already joined. The same list, so Home is never
    // empty while Discover is not.
    api
      .callApi("discover", { params: { page_size: 8 }, suppressError: true })
      .then((res: { results?: PublicProject[] } | PublicProject[] | undefined) => {
        const list = Array.isArray(res) ? res : (res?.results ?? []);
        setProjects((list as PublicProject[]).filter((p) => !p.already_joined));
      })
      .catch(() => setProjects([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [api, user?.id]);

  const known = projects !== null;
  const openProjects = projects ?? [];
  const joinedProjects = joined ?? [];

  const seeAll = (
    <button
      type="button"
      className="lsf-home-page__link"
      onClick={() => spaNavigate("/discover")}
      style={{ background: "none", border: 0, cursor: "pointer" }}
    >
      {t("pages.home.home_page.see_all", undefined, "See all →")}
    </button>
  );

  return (
    <main className="lsf-home-page">
      <section className="lsf-home-page__hero">
        <div className="lsf-home-page__hero-copy">
          <h1 className="lsf-home-page__hero-title">
            {t("pages.home.home_page.hi_name", { name: displayName }, `Hi ${displayName}`)}
          </h1>
          <p className="lsf-home-page__hero-sub">
            {t(
              "pages.home.home_page.orgless_sub",
              undefined,
              "You're not in an organization yet. Join an open project below to start contributing.",
            )}
          </p>
        </div>
        <div className="lsf-home-page__hero-actions">
          <Button size="medium" onClick={() => spaNavigate("/discover")}>
            {t("pages.home.home_page.discover_open_projects", undefined, "Discover open projects")}
          </Button>
          {user?.id ? (
            <Button size="medium" look="outlined" onClick={() => spaNavigate(`/users/${user.id}`)}>
              {t("pages.home.home_page.my_profile", undefined, "My profile")}
            </Button>
          ) : null}
        </div>
      </section>

      {joinedProjects.length > 0 && (
        <section className="lsf-home-page__sec">
          <div className="lsf-home-page__sec-head">
            <h2 className="lsf-home-page__sec-title">
              {t("pages.home.home_page.your_projects", undefined, "Your projects")}
            </h2>
            {joinedProjects.length > 4 && seeAll}
          </div>
          <div className="lsf-home-page__pubgrid">
            {joinedProjects.slice(0, 4).map((p) => (
              <PubCard
                key={p.id}
                t={t}
                p={p}
                action={
                  <Button
                    variant="primary"
                    look="outlined"
                    size="small"
                    onClick={() => spaNavigate(`/projects/${p.id}`)}
                  >
                    {t("pages.home.home_page.open_label", undefined, "Open")}
                  </Button>
                }
              />
            ))}
          </div>
        </section>
      )}

      <section className="lsf-home-page__sec">
        <div className="lsf-home-page__sec-head">
          <h2 className="lsf-home-page__sec-title">
            {t("pages.home.home_page.public_projects", undefined, "Public projects")}
          </h2>
          {openProjects.length > 0 && seeAll}
        </div>

        {!known ? (
          <div className="lsf-home-page__pubgrid" aria-hidden>
            {[0, 1, 2].map((i) => (
              <div key={i} className="lsf-home-page__pubcard" style={{ opacity: 0.5, minHeight: 132 }} />
            ))}
          </div>
        ) : openProjects.length === 0 ? (
          <div className="lsf-home-page__empty">
            <h3>{t("pages.home.home_page.no_public_projects", undefined, "No public projects right now")}</h3>
            <p>
              {t(
                "pages.home.home_page.no_public_projects_body",
                undefined,
                "When a team makes a project public, it shows here and on Discover.",
              )}
            </p>
          </div>
        ) : (
          <div className="lsf-home-page__pubgrid">
            {openProjects.slice(0, 4).map((p) => (
              <PubCard
                key={p.id}
                t={t}
                p={p}
                action={
                  p.join_status === "pending" ? (
                    <Button variant="neutral" look="outlined" size="small" disabled>
                      {t("pages.home.home_page.requested_label", undefined, "Requested")}
                    </Button>
                  ) : p.contribution_mode !== "open" ? (
                    <Button variant="neutral" look="outlined" size="small" disabled>
                      {t("pages.home.home_page.members_only_label", undefined, "Members only")}
                    </Button>
                  ) : (
                    <Button variant="primary" size="small" onClick={() => setJoinTarget(p)}>
                      {t("pages.home.home_page.join_label", undefined, "Join")}
                    </Button>
                  )
                }
              />
            ))}
          </div>
        )}
      </section>

      {/* Promo row: contribute-a-language + invite hint, side by side. */}
      <section className="lsf-home-page__sec">
        <div className="lsf-home-page__promos">
          <div className="lsf-home-page__discover">
            <div>
              <h3 className="lsf-home-page__discover-title">
                {t("pages.home.home_page.contribute_language_title", undefined, "Contribute a language")}
              </h3>
              <p className="lsf-home-page__discover-text">
                {t(
                  "pages.home.home_page.contribute_language_text",
                  undefined,
                  "Speak a language we don't support yet? Request it and help translate the interface with other volunteers.",
                )}
              </p>
            </div>
            <Button look="outlined" size="small" onClick={() => setRequestLangOpen(true)}>
              {t("pages.home.home_page.request_language_arrow", undefined, "Request a language →")}
            </Button>
          </div>

          <div className="lsf-home-page__discover">
            <div>
              <h3 className="lsf-home-page__discover-title">
                {t("pages.home.home_page.invite_hint_title", undefined, "Invited to a team?")}
              </h3>
              <p className="lsf-home-page__discover-text">
                {t(
                  "pages.home.home_page.invite_hint_text",
                  undefined,
                  "Open the invitation link on this device to join your organization.",
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="lsf-home-page__util">
        <nav className="lsf-home-page__util-nav">
          {user?.id && <a onClick={() => spaNavigate(`/users/${user.id}`)}>{t("nav.account", undefined, "Account")}</a>}
          {user?.id && (
            <a onClick={() => spaNavigate(`/users/${user.id}/consents`)}>
              {t("pages.home.home_page.my_consents", undefined, "My consents")}
            </a>
          )}
          {branding.docs_url && (
            <a href={branding.docs_url} target="_blank" rel="noreferrer">
              {t("pages.home.home_page.documentation", undefined, "Documentation")}
            </a>
          )}
          {branding.discord_url && (
            <a href={branding.discord_url} target="_blank" rel="noreferrer">
              Discord
            </a>
          )}
        </nav>
        <span className="lsf-home-page__brand">
          <img className="lsf-home-page__brand-logo" src={logoSrc} alt={`${branding.name} logo`} />
        </span>
      </footer>

      {requestLangOpen && <RequestLanguageModal onClose={() => setRequestLangOpen(false)} />}

      {joinTarget && (
        <JoinProjectModal
          project={joinTarget}
          onClose={() => setJoinTarget(null)}
          onJoined={(id, status) => {
            setJoinTarget(null);
            if (status === "pending") {
              setProjects((prev) => prev?.map((p) => (p.id === id ? { ...p, join_status: "pending" } : p)) ?? prev);
              return;
            }
            spaNavigate(`/projects/${id}`);
          }}
        />
      )}
    </main>
  );
};

export default OrglessHome;
